/**
 * Modal de détail d'une worksheet avec liste des tâches
 */

import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import {
  Worksheet,
  WorksheetTask,
  TaskStatus,
  ActionType,
  SkipReason,
} from '../../types/worksheets.types';
import { getWorksheet } from '../../services/worksheets.api';

// Status colors for tasks
const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  TODO: 'bg-gray-100 text-gray-700 border-gray-300',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  DONE: 'bg-green-100 text-green-700 border-green-300',
  SKIPPED: 'bg-orange-100 text-orange-700 border-orange-300',
};

// Labels for task status (used in future updates)
// const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
//   TODO: 'A faire',
//   IN_PROGRESS: 'En cours',
//   DONE: 'Terminée',
//   SKIPPED: 'Ignorée',
// };

const ACTION_TYPE_ICONS: Record<ActionType, string> = {
  CHECK: 'M9 12l2 2 4-4',
  VALIDATE: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944',
  COUNT: 'M7 20l4-16m2 16l4-16',
  MOVE: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4',
  PRINT: 'M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2',
  SCAN: 'M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01',
  WEIGH: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9',
  PHOTO: 'M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86',
  SIGN: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z',
  FREE_TEXT: 'M4 6h16M4 12h16M4 18h7',
};

const SKIP_REASON_LABELS: Record<SkipReason, string> = {
  NOT_APPLICABLE: 'Non applicable',
  MISSING_MATERIAL: 'Matériel manquant',
  ALREADY_DONE: 'Déjà fait',
  MANAGER_DECISION: 'Décision manager',
  OTHER: 'Autre raison',
};

interface WorksheetDetailModalProps {
  worksheetId: string;
  onClose: () => void;
}

export default function WorksheetDetailModal({
  worksheetId,
  onClose,
}: WorksheetDetailModalProps) {
  const [worksheet, setWorksheet] = useState<Worksheet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadWorksheet = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getWorksheet(worksheetId);
        setWorksheet(data);
      } catch (err) {
        console.error('Failed to load worksheet:', err);
        setError('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };

    loadWorksheet();
  }, [worksheetId]);

  if (loading) {
    return (
      <Modal isOpen={true} onClose={onClose} title="Chargement..." size="lg">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
        </div>
      </Modal>
    );
  }

  if (error || !worksheet) {
    return (
      <Modal isOpen={true} onClose={onClose} title="Erreur" size="md">
        <div className="py-8 text-center text-red-600">
          {error || 'Worksheet introuvable'}
        </div>
      </Modal>
    );
  }

  const tasks = worksheet.tasks || [];
  const completedTasks = tasks.filter(
    (t) => t.status === 'DONE' || t.status === 'SKIPPED'
  ).length;
  const progressPercent = tasks.length > 0 ? (completedTasks / tasks.length) * 100 : 0;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={worksheet.title}
      size="lg"
      footer={
        <Button variant="outline" onClick={onClose}>
          Fermer
        </Button>
      }
    >
      <div className="space-y-6">
        {/* Header info */}
        <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
          <div>
            <span className="text-sm text-gray-500">Statut</span>
            <div className="font-medium text-gray-900">{worksheet.status}</div>
          </div>
          <div>
            <span className="text-sm text-gray-500">Assigné à</span>
            <div className="font-medium text-gray-900">
              {worksheet.assigned_to_name || '-'}
            </div>
          </div>
          <div>
            <span className="text-sm text-gray-500">Créée le</span>
            <div className="font-medium text-gray-900">
              {format(new Date(worksheet.created_at), "dd/MM/yyyy 'à' HH:mm", {
                locale: fr,
              })}
            </div>
          </div>
          <div>
            <span className="text-sm text-gray-500">Validée par</span>
            <div className="font-medium text-gray-900">
              {worksheet.validated_by_name || '-'}
            </div>
          </div>
        </div>

        {/* Description */}
        {worksheet.description && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">{worksheet.description}</p>
          </div>
        )}

        {/* Rejection reason */}
        {worksheet.rejection_reason && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-red-800 mb-1">
              Raison du rejet
            </h4>
            <p className="text-sm text-red-700">{worksheet.rejection_reason}</p>
          </div>
        )}

        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Progression
            </span>
            <span className="text-sm text-gray-500">
              {completedTasks}/{tasks.length} tâches
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-green-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Tasks list */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            Liste des tâches
          </h3>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {tasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Aucune tâche dans cette worksheet
              </div>
            ) : (
              tasks.map((task, index) => (
                <TaskCard key={task.id} task={task} index={index} />
              ))
            )}
          </div>
        </div>

        {/* Completion info */}
        {worksheet.completed_at && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <svg
              className="w-6 h-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <div className="font-medium text-green-800">Terminée</div>
              <div className="text-sm text-green-700">
                {format(
                  new Date(worksheet.completed_at),
                  "dd/MM/yyyy 'à' HH:mm",
                  { locale: fr }
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// Task card component
function TaskCard({ task, index }: { task: WorksheetTask; index: number }) {
  const iconPath = ACTION_TYPE_ICONS[task.action_type] || ACTION_TYPE_ICONS.CHECK;

  return (
    <div
      className={`border rounded-lg p-3 ${TASK_STATUS_COLORS[task.status]}`}
    >
      <div className="flex items-start gap-3">
        {/* Status icon */}
        <div className="flex-shrink-0 mt-0.5">
          {task.status === 'DONE' ? (
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : task.status === 'SKIPPED' ? (
            <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          ) : task.status === 'IN_PROGRESS' ? (
            <div className="w-5 h-5 border-2 border-yellow-500 rounded-full animate-pulse" />
          ) : (
            <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 font-medium">
              #{index + 1}
            </span>
            <h4 className="font-medium text-gray-900 truncate">{task.title}</h4>
          </div>
          {task.instructions && (
            <p className="text-sm text-gray-600 mt-1">{task.instructions}</p>
          )}

          {/* Result or skip info */}
          {task.status === 'DONE' && task.result_value && (
            <div className="mt-2 text-sm bg-white/50 rounded px-2 py-1">
              <span className="text-gray-500">Résultat:</span>{' '}
              <span className="font-medium">{task.result_value}</span>
            </div>
          )}
          {task.status === 'SKIPPED' && (
            <div className="mt-2 text-sm">
              <span className="text-orange-700">
                Ignorée: {SKIP_REASON_LABELS[task.skipped_reason!]}
              </span>
              {task.skipped_comment && (
                <span className="text-gray-600"> - {task.skipped_comment}</span>
              )}
            </div>
          )}

          {/* Timing */}
          {task.completed_at && (
            <div className="mt-1 text-xs text-gray-500">
              {format(new Date(task.completed_at), 'HH:mm', { locale: fr })}
            </div>
          )}
        </div>

        {/* Action type badge */}
        <div className="flex-shrink-0">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
          </svg>
        </div>
      </div>
    </div>
  );
}
